import React, { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CrudService from "services/cruds-service";
import { jwtDecode } from "jwt-decode";
import { useParams, useNavigate } from "react-router-dom";
const APPROVER_EMAIL =
    process.env.REACT_APP_ANNUAL_LEAVE_APPROVER ||
    "mehmet.ikmaz@filenders.com, olcay.gulsen@filenders.com";

const ANNUAL_LEAVE_LIMIT = 14;
const MAX_LEAVE_PER_REQUEST = 7;

const FRONTEND_URL =
    process.env.REACT_APP_FRONTEND_URL || window.location.origin;

const ANNUAL_LEAVE_LIST_URL = `${FRONTEND_URL}/annual-leave/list`;

const AnnualLeaveForm = () => {
    const currentYear = new Date().getFullYear();
    const { id } = useParams();
    const navigate = useNavigate();
    const isDocumentMode = Boolean(id);

    const [leaveDocumentFile, setLeaveDocumentFile] = useState(null);
    const [existingLeaveDocument, setExistingLeaveDocument] = useState("");
    const [loading, setLoading] = useState(false);
    const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
    const [annualLeaveRequests, setAnnualLeaveRequests] = useState([]);

    const [emailPreviewData, setEmailPreviewData] = useState({
        to: "",
        cc: "",
        subject: "",
        body: "",
    });

    const [formData, setFormData] = useState({
        employeeName: "",
        employeeEmail: "",
        department: "",
        leaveType: "annual",
        startDate: "",
        endDate: "",
        totalDays: "",
        description: "",
    });

    const [snackbar, setSnackbar] = useState({
        open: false,
        severity: "success",
        message: "",
    });

    const normalizeItem = (item) =>
        item?.attributes ? { id: item.id, ...item.attributes } : item;

    const isAnnualLeave = (item) => {
        return (
            !item?.leaveType ||
            item.leaveType === "annual" ||
            item.leaveType === "Annual Leave"
        );
    };

    const getYearFromDate = (date) => {
        if (!date) return null;
        return new Date(date).getFullYear();
    };

    const calculateBusinessDays = (startDate, endDate) => {
        if (!startDate || !endDate) return "";

        const start = new Date(startDate);
        const end = new Date(endDate);

        if (end < start) return "";

        let count = 0;
        const current = new Date(start);

        while (current <= end) {
            const day = current.getDay();

            if (day !== 0 && day !== 6) {
                count += 1;
            }

            current.setDate(current.getDate() + 1);
        }

        return count;
    };

    const fetchCurrentUser = async () => {
        const token = localStorage.getItem("token");

        if (!token) return;

        try {
            const decoded = jwtDecode(token);
            const userId = decoded?.id;
            const email = decoded?.email || "";

            let employeeName = "";
            let department = "";

            if (userId) {
                const response = await CrudService.getUser(userId);
                const user =
                    response?.data?.data?.attributes ||
                    response?.data?.attributes ||
                    response?.data;

                employeeName =
                    user?.name ||
                    user?.fullName ||
                    user?.username ||
                    user?.firstName ||
                    "";

                department = user?.department || user?.title || "";
            }

            setFormData((prev) => ({
                ...prev,
                employeeName,
                employeeEmail: email,
                department,
            }));
        } catch (error) {
            console.error("Current user fetch error:", error);
        }
    };

    const fetchAnnualLeaveRequests = async () => {
        try {
            const response = await CrudService.getAnnualLeaveRequests();
            const list = response?.data?.data || response?.data || [];
            const formattedList = Array.isArray(list) ? list.map(normalizeItem) : [];

            setAnnualLeaveRequests(formattedList);
        } catch (error) {
            console.error("Annual leave requests fetch error:", error);
        }
    };

    useEffect(() => {
        if (isDocumentMode) {
            fetchAnnualLeaveById();
        } else {
            fetchCurrentUser();
        }

        fetchAnnualLeaveRequests();

        const interval = setInterval(() => {
            fetchAnnualLeaveRequests();
        }, 5000);

        const handleFocus = () => {
            fetchAnnualLeaveRequests();
        };

        window.addEventListener("focus", handleFocus);

        return () => {
            clearInterval(interval);
            window.removeEventListener("focus", handleFocus);
        };
    }, [id]);
    const usedApprovedDays = useMemo(() => {
        return annualLeaveRequests
            .filter((item) => item.employeeEmail === formData.employeeEmail)
            .filter((item) => isAnnualLeave(item))
            .filter((item) => item.status === "approved")
            .filter((item) => getYearFromDate(item.startDate) === currentYear)
            .reduce((sum, item) => sum + Number(item.totalDays || 0), 0);
    }, [annualLeaveRequests, formData.employeeEmail, currentYear]);

    const pendingDays = useMemo(() => {
        return annualLeaveRequests
            .filter((item) => item.employeeEmail === formData.employeeEmail)
            .filter((item) => isAnnualLeave(item))
            .filter((item) => item.status === "pending")
            .filter((item) => getYearFromDate(item.startDate) === currentYear)
            .reduce((sum, item) => sum + Number(item.totalDays || 0), 0);
    }, [annualLeaveRequests, formData.employeeEmail, currentYear]);

    const remainingAnnualLeaveDays = Math.max(
        ANNUAL_LEAVE_LIMIT - usedApprovedDays,
        0
    );

    const requestedDays = Number(formData.totalDays || 0);

    const remainingAfterRequest =
        formData.leaveType === "annual"
            ? remainingAnnualLeaveDays - requestedDays
            : remainingAnnualLeaveDays;

    const usageRate = Math.min(
        (usedApprovedDays / ANNUAL_LEAVE_LIMIT) * 100,
        100
    );

    const normalizeEmailList = (emails = []) => {
        return Array.from(
            new Set(
                emails
                    .flatMap((item) => String(item || "").split(","))
                    .map((item) => item.trim())
                    .filter(Boolean)
            )
        ).join(", ");
    };

    const plainTextVersion = (html) =>
        html.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();

    const getAuthUserEmail = () => {
        const token = localStorage.getItem("token");

        if (!token) return "";

        try {
            const decoded = jwtDecode(token);
            return decoded?.email || decoded?.user?.email || "";
        } catch {
            return "";
        }
    };

    const showActionFeedback = (message, severity = "success") => {
        setSnackbar({
            open: true,
            severity,
            message,
        });

        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const formatDate = (date) => {
        if (!date) return "—";
        return new Date(date).toLocaleDateString("en-GB");
    };

    const getLeaveTypeLabel = (value) => {
        switch (value) {
            case "annual":
            case "Annual Leave":
            case undefined:
            case null:
            case "":
                return "Annual Leave";
            case "excuse":
                return "Excuse Leave";
            case "unpaid":
                return "Unpaid Leave";
            case "sick":
                return "Sick Leave";
            default:
                return value || "Annual Leave";
        }
    };

    const sendAnnualLeaveMail = async ({ to, cc = "", subject, body }) => {
        const cleanTo = String(to || "").trim();
        const cleanCc = String(cc || "").trim();

        if (!cleanTo) {
            throw new Error("Recipient email is missing.");
        }

        const payload = {
            to: cleanTo,
            subject,
            body,
            text: plainTextVersion(body),
        };

        if (cleanCc) {
            payload.cc = cleanCc;
        }

        const res = await fetch(
            `${process.env.REACT_APP_API_URL}/email/Send-meeting-recap`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
                body: JSON.stringify(payload),
            }
        );

        const result = await res.json();

        if (!res.ok) {
            throw new Error(result?.error || result?.message || "Email could not be sent.");
        }

        return result;
    };
    const buildApprovalRequestMailBody = (payload) => {
        return `
      <div style="font-family:Segoe UI,Arial,sans-serif;font-size:14px;color:#333;line-height:1.6;">
        <h3>New Annual Leave Request</h3>

        <table style="border-collapse:collapse;width:100%;">
          <tr>
            <td style="padding:6px 10px;border:1px solid #ccc;width:35%;"><b>Employee</b></td>
            <td style="padding:6px 10px;border:1px solid #ccc;">${payload.employeeName || "—"}</td>
          </tr>
          <tr>
            <td style="padding:6px 10px;border:1px solid #ccc;"><b>Email</b></td>
            <td style="padding:6px 10px;border:1px solid #ccc;">${payload.employeeEmail || "—"}</td>
          </tr>
          <tr>
            <td style="padding:6px 10px;border:1px solid #ccc;"><b>Department / Title</b></td>
            <td style="padding:6px 10px;border:1px solid #ccc;">${payload.department || "—"}</td>
          </tr>
          <tr>
            <td style="padding:6px 10px;border:1px solid #ccc;"><b>Leave Type</b></td>
            <td style="padding:6px 10px;border:1px solid #ccc;">${getLeaveTypeLabel(payload.leaveType)}</td>
          </tr>
          <tr>
            <td style="padding:6px 10px;border:1px solid #ccc;"><b>Start Date</b></td>
            <td style="padding:6px 10px;border:1px solid #ccc;">${formatDate(payload.startDate)}</td>
          </tr>
          <tr>
            <td style="padding:6px 10px;border:1px solid #ccc;"><b>End Date</b></td>
            <td style="padding:6px 10px;border:1px solid #ccc;">${formatDate(payload.endDate)}</td>
          </tr>
          <tr>
            <td style="padding:6px 10px;border:1px solid #ccc;"><b>Total Business Days</b></td>
            <td style="padding:6px 10px;border:1px solid #ccc;">${payload.totalDays || "—"}</td>
          </tr>
          <tr>
            <td style="padding:6px 10px;border:1px solid #ccc;"><b>Remaining Balance</b></td>
            <td style="padding:6px 10px;border:1px solid #ccc;">${Math.max(remainingAfterRequest, 0)} business days</td>
          </tr>
          <tr>
            <td style="padding:6px 10px;border:1px solid #ccc;"><b>Description</b></td>
            <td style="padding:6px 10px;border:1px solid #ccc;">${payload.description || "—"}</td>
          </tr>
        </table>

        <br />

        <p>
          A single request can include a maximum of ${MAX_LEAVE_PER_REQUEST} business days.
          The annual leave entitlement is ${ANNUAL_LEAVE_LIMIT} business days and does not carry over to the next year.
        </p>
        
        <p>
  Please open the Annual Leave Approval List using the button below and approve or reject this request.
</p>

<div style="text-align:center;margin:24px 0;">
  <a
    href="${ANNUAL_LEAVE_LIST_URL}"
    target="_blank"
    style="
      display:inline-block;
      background:#1e40af;
      color:#ffffff;
      padding:12px 22px;
      border-radius:8px;
      text-decoration:none;
      font-weight:600;
    "
  >
    Open Annual Leave Approval List
  </a>
</div>
      </div>
    `;

    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        const updatedData = {
            ...formData,
            [name]: value,
        };

        if (name === "startDate" || name === "endDate") {
            updatedData.totalDays = calculateBusinessDays(
                updatedData.startDate,
                updatedData.endDate
            );
        }

        setFormData(updatedData);
    };

    const validateForm = () => {
        const selectedYear = getYearFromDate(formData.startDate);

        if (!formData.employeeName.trim()) {
            showActionFeedback("Employee name could not be found.", "error");
            return false;
        }

        if (!formData.employeeEmail.trim()) {
            showActionFeedback("Employee email could not be found.", "error");
            return false;
        }

        if (!formData.leaveType) {
            showActionFeedback("Leave type is required.", "error");
            return false;
        }

        if (!formData.startDate || !formData.endDate) {
            showActionFeedback("Start date and end date are required.", "error");
            return false;
        }

        if (getYearFromDate(formData.startDate) !== getYearFromDate(formData.endDate)) {
            showActionFeedback(
                "The start date and end date must be within the same year.",
                "error"
            );
            return false;
        }

        if (selectedYear !== currentYear) {
            showActionFeedback(
                `You can only create a leave request for ${currentYear}.`,
                "error"
            );
            return false;
        }

        if (!formData.totalDays || Number(formData.totalDays) <= 0) {
            showActionFeedback("The selected date range is not valid.", "error");
            return false;
        }

        if (formData.leaveType === "annual") {
            if (Number(formData.totalDays) > MAX_LEAVE_PER_REQUEST) {
                showActionFeedback(
                    `A single request can include a maximum of ${MAX_LEAVE_PER_REQUEST} business days.`,
                    "error"
                );
                return false;
            }

            if (Number(formData.totalDays) > remainingAnnualLeaveDays) {
                showActionFeedback(
                    `Insufficient leave balance. Your remaining annual leave balance is ${remainingAnnualLeaveDays} business days.`,
                    "error"
                );
                return false;
            }
        }

        return true;
    };

    const submitHandler = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        const payload = {
            ...formData,
            totalDays: Number(formData.totalDays),
            year: getYearFromDate(formData.startDate),
            status: "pending",
        };

        try {
            setLoading(true);

            await CrudService.createAnnualLeaveRequest({
                data: payload,
            });

            await fetchAnnualLeaveRequests();

            const authEmail = getAuthUserEmail();

            setEmailPreviewData({
                to: APPROVER_EMAIL,
                cc: "",
                subject: `New Annual Leave Request - ${payload.employeeName}`,
                body: buildApprovalRequestMailBody(payload),
            });

            setEmailPreviewOpen(true);

            showActionFeedback(
                "Annual leave request has been created. Please review and send the email notification.",
                "success"
            );
        } catch (error) {
            console.error("Annual leave request error:", error);
            showActionFeedback(
                error.message || "An error occurred while creating the request.",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    const confirmAndSendAnnualLeaveMail = async () => {
        const { to, cc, subject, body } = emailPreviewData;

        try {
            setLoading(true);

            await sendAnnualLeaveMail({
                to,
                cc,
                subject,
                body,
            });

            setEmailPreviewOpen(false);

            showActionFeedback("Annual leave approval email has been sent successfully.", "success");

            setFormData((prev) => ({
                ...prev,
                leaveType: "annual",
                startDate: "",
                endDate: "",
                totalDays: "",
                description: "",
            }));

            await fetchAnnualLeaveRequests();
        } catch (error) {
            console.error("Annual leave mail error:", error);
            showActionFeedback(error.message || "Email could not be sent.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleDocumentChange = (e) => {
        const file = e.target.files?.[0];

        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            showActionFeedback(
                "Maximum file size is 10 MB.",
                "error"
            );
            return;
        }

        const allowedTypes = [
            "application/pdf",
            "image/jpeg",
            "image/jpg",
            "image/png",
        ];

        if (!allowedTypes.includes(file.type)) {
            showActionFeedback("Only PDF, JPG, JPEG and PNG files are allowed.", "error");
            return;
        }

        setLeaveDocumentFile(file);
    };

    const fetchAnnualLeaveById = async () => {
        if (!id) return;

        try {
            setLoading(true);

            const response = await CrudService.getAnnualLeaveRequestById(id);
            const item =
                response?.data?.data?.attributes ||
                response?.data?.attributes ||
                response?.data?.data ||
                response?.data;

            if (!item) {
                showActionFeedback("Annual leave request could not be found.", "error");
                return;
            }

            setFormData({
                employeeName: item.employeeName || "",
                employeeEmail: item.employeeEmail || "",
                department: item.department || "",
                leaveType: item.leaveType || "annual",
                startDate: item.startDate ? item.startDate.slice(0, 10) : "",
                endDate: item.endDate ? item.endDate.slice(0, 10) : "",
                totalDays: item.totalDays || "",
                description: item.description || "",
            });
            if (item.status !== "approved") {
                showActionFeedback(
                    "Documents can only be uploaded for approved leave requests.",
                    "error"
                );

                navigate("/annual-leave/list");
                return;
            }
            setExistingLeaveDocument(item.leaveDocument || "");
        } catch (error) {
            console.error("Annual leave detail error:", error);
            showActionFeedback("Annual leave request could not be loaded.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleUploadDocument = async () => {
        if (!id) return;

        if (!leaveDocumentFile) {
            showActionFeedback("Please select a document before uploading.", "error");
            return;
        }

        try {
            setLoading(true);

            const payload = new FormData();
            payload.append("leaveDocument", leaveDocumentFile);

            await CrudService.uploadAnnualLeaveDocument(id, payload);

            showActionFeedback("Leave document uploaded successfully.", "success");

            await fetchAnnualLeaveById();

            setTimeout(() => {
                navigate("/annual-leave/list");
            }, 800);
        } catch (error) {
            console.error("Leave document upload error:", error);
            showActionFeedback(
                error.message || "Document could not be uploaded.",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{ mt: 8, mr: 2, zIndex: 9999 }}
            >
                <Alert
                    severity={snackbar.severity}
                    variant="filled"
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    sx={{
                        minWidth: 360,
                        maxWidth: 520,
                        fontSize: "15px",
                        fontWeight: 600,
                        borderRadius: "12px",
                        boxShadow: "0 12px 35px rgba(0,0,0,0.25)",
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <MDBox
                mb={3}
                sx={{
                    p: 3,
                    borderRadius: "10px",
                    background:
                        "rgba(15,23,42,0.98)",
                    color: "#fff",
                    boxShadow: "0 18px 45px rgba(15,23,42,0.22)",
                }}
            >
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={8}>
                        <MDTypography variant="h3" fontWeight="bold" color="white">
                            Annual Leave Request Form
                        </MDTypography>

                        <MDTypography variant="button" color="white" sx={{ opacity: 0.85 }}>
                            Create your annual leave request in line with the corporate leave policy
                            and start the approval process.
                        </MDTypography>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <MDBox
                            sx={{
                                p: 2,
                                borderRadius: "10px",
                                backgroundColor: "rgba(255,255,255,0.12)",
                                border: "1px solid rgba(255,255,255,0.22)",
                            }}
                        >
                            <MDTypography variant="button" color="white">
                                Active Period
                            </MDTypography>

                            <MDTypography variant="h4" fontWeight="bold" color="white">
                                {currentYear}
                            </MDTypography>
                        </MDBox>
                    </Grid>
                </Grid>
            </MDBox>

            <Grid container spacing={3} mb={3}>
                <Grid item xs={12} md={3}>
                    <Card sx={{ p: 2.5, borderRadius: "10px", height: "100%" }}>
                        <MDTypography variant="button" color="text">
                            Annual Entitlement
                        </MDTypography>
                        <MDTypography variant="h4" fontWeight="bold" color="success">
                            {ANNUAL_LEAVE_LIMIT}
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                            business days / year
                        </MDTypography>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card sx={{ p: 2.5, borderRadius: "10px", height: "100%" }}>
                        <MDTypography variant="button" color="text">
                            Approved Used
                        </MDTypography>
                        <MDTypography variant="h4" fontWeight="bold" color="error">
                            {usedApprovedDays}
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                            for {currentYear}
                        </MDTypography>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card sx={{ p: 2.5, borderRadius: "10px", height: "100%" }}>
                        <MDTypography variant="button" color="text">
                            Pending Requests
                        </MDTypography>
                        <MDTypography variant="h4" fontWeight="bold" color="warning">
                            {pendingDays}
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                            business days awaiting approval
                        </MDTypography>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card sx={{ p: 2.5, borderRadius: "10px", height: "100%" }}>
                        <MDTypography variant="button" color="text">
                            Remaining Balance
                        </MDTypography>
                        <MDTypography variant="h4" fontWeight="bold" color="info">
                            {remainingAnnualLeaveDays}
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                            available business days
                        </MDTypography>
                    </Card>
                </Grid>
            </Grid>

            <MDBox
                mb={3}
                sx={{
                    p: 3,
                    borderRadius: "10px",
                    bgcolor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                }}
            >
                <MDTypography variant="h6" fontWeight="bold" mb={2}>
                    Corporate Leave Policy
                </MDTypography>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <Alert severity="info" sx={{ height: "100%" }}>
                            Each employee has 14 business days of annual leave.
                        </Alert>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Alert severity="warning" sx={{ height: "100%" }}>
                            A single request can include a maximum of 7 business days.
                        </Alert>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Alert severity="info" sx={{ height: "100%" }}>
                            Leave balances reset at year-end and do not carry over.
                        </Alert>
                    </Grid>
                </Grid>
            </MDBox>

            <MDBox
                mb={12}
                sx={{
                    p: 3,
                    borderRadius: "10px",
                    bgcolor: "background.paper",
                    boxShadow: "none",
                }}
            >
                <MDBox mb={3}>
                    <MDTypography variant="h5" fontWeight="bold">
                        Request Details
                    </MDTypography>

                    <MDTypography variant="caption" color="text">
                        Total business days are calculated automatically. Saturdays and Sundays
                        are excluded.
                    </MDTypography>
                </MDBox>

                <Divider sx={{ mb: 3 }} />

                <form onSubmit={submitHandler}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="Employee Name"
                                name="employeeName"
                                value={formData.employeeName}
                                fullWidth
                                required
                                disabled={isDocumentMode}
                                InputProps={{ readOnly: true }}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                label="Employee Email"
                                name="employeeEmail"
                                type="email"
                                value={formData.employeeEmail}
                                fullWidth
                                required
                                disabled={isDocumentMode}
                                InputProps={{ readOnly: true }}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                label="Department / Title"
                                name="department"
                                value={formData.department}
                                onChange={handleChange}
                                fullWidth
                                disabled={isDocumentMode}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                select
                                label="Leave Type"
                                name="leaveType"
                                value={formData.leaveType}
                                onChange={handleChange}
                                fullWidth
                                required
                                disabled={isDocumentMode}
                                InputLabelProps={{ shrink: true }}
                            >
                                <MenuItem value="annual">Annual Leave</MenuItem>
                                <MenuItem value="excuse">Excuse Leave</MenuItem>
                                <MenuItem value="unpaid">Unpaid Leave</MenuItem>
                                <MenuItem value="sick">Sick Leave</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                label="Leave Start Date"
                                name="startDate"
                                type="date"
                                value={formData.startDate}
                                onChange={handleChange}
                                fullWidth
                                required
                                disabled={isDocumentMode}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                label="Leave End Date"
                                name="endDate"
                                type="date"
                                value={formData.endDate}
                                onChange={handleChange}
                                fullWidth
                                required
                                disabled={isDocumentMode}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                label="Total Business Days"
                                name="totalDays"
                                value={formData.totalDays}
                                fullWidth
                                InputProps={{ readOnly: true }}
                                disabled={isDocumentMode}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                label="Remaining Balance After Request"
                                value={
                                    formData.leaveType === "annual" && requestedDays
                                        ? `${Math.max(remainingAfterRequest, 0)} business days`
                                        : `${remainingAnnualLeaveDays} business days`
                                }
                                fullWidth
                                InputProps={{ readOnly: true }}
                                InputLabelProps={{ shrink: true }}
                                disabled={isDocumentMode}
                                error={formData.leaveType === "annual" && remainingAfterRequest < 0}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <MDBox
                                sx={{
                                    p: 2,
                                    borderRadius: "14px",
                                    border: "1px solid #e2e8f0",
                                    bgcolor: "#f8fafc",
                                    height: "100%",
                                }}
                            >
                                <MDTypography variant="button" color="text">
                                    Usage Rate
                                </MDTypography>

                                <MDTypography variant="h6" fontWeight="bold">
                                    {usageRate.toFixed(0)}%
                                </MDTypography>

                                <MDBox
                                    mt={1}
                                    sx={{
                                        height: 8,
                                        borderRadius: 99,
                                        bgcolor: "#e5e7eb",
                                        overflow: "hidden",
                                    }}
                                >
                                    <MDBox
                                        sx={{
                                            width: `${usageRate}%`,
                                            height: "100%",
                                            bgcolor:
                                                usageRate >= 80
                                                    ? "#ef4444"
                                                    : usageRate >= 50
                                                        ? "#f59e0b"
                                                        : "#22c55e",
                                        }}
                                    />
                                </MDBox>

                                <Chip
                                    label={`${remainingAnnualLeaveDays} days available`}
                                    color="info"
                                    size="small"
                                    sx={{ mt: 1.5 }}
                                />
                            </MDBox>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="Description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                fullWidth
                                multiline
                                disabled={isDocumentMode}
                                minRows={3}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        {isDocumentMode && (
                            <Grid item xs={12}>
                                <Card
                                    sx={{
                                        p: 3,
                                        borderRadius: "10px",
                                        border: "1px solid #e2e8f0",
                                        bgcolor: "#f8fafc",
                                    }}
                                >
                                    <MDTypography variant="h6" fontWeight="bold" mb={1}>
                                        Leave Document Upload
                                    </MDTypography>

                                    <MDTypography variant="caption" color="text">
                                        Upload the approved leave document. Accepted formats: PDF, JPG, JPEG, PNG.
                                    </MDTypography>

                                    <MDBox mt={2}>
                                        <input
                                            type="file"
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            onChange={handleDocumentChange}
                                        />
                                    </MDBox>

                                    {existingLeaveDocument && (
                                        <MDBox mt={2}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() =>
                                                    window.open(
                                                        `${process.env.REACT_APP_API_URL}${existingLeaveDocument}`,
                                                        "_blank"
                                                    )
                                                }
                                                sx={{
                                                    backgroundColor: "#1e40af",
                                                    color: "#fff",
                                                    px: 4,
                                                    "&:hover": {
                                                        backgroundColor: "#1d4ed8",
                                                    },
                                                }}
                                            >
                                                View Existing Document
                                            </Button>
                                        </MDBox>
                                    )}
                                </Card>
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <MDBox display="flex" justifyContent="flex-end" gap={2}>
                                {isDocumentMode ? (
                                    <Button
                                        type="button"
                                        variant="contained"
                                        disabled={loading}
                                        onClick={handleUploadDocument}
                                        sx={{
                                            backgroundColor: "#1e40af",
                                            color: "#fff",
                                            px: 4,
                                            "&:hover": {
                                                backgroundColor: "#1d4ed8",
                                            },
                                        }}
                                    >
                                        {loading ? "Uploading..." : "Upload Document"}
                                    </Button>
                                ) : (
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={loading}
                                        sx={{
                                            backgroundColor: "#1e40af",
                                            color: "#fff",
                                            px: 4,
                                            "&:hover": {
                                                backgroundColor: "#1d4ed8",
                                            },
                                        }}
                                    >
                                        {loading ? "Submitting..." : "Submit Request"}
                                    </Button>
                                )}
                            </MDBox>
                        </Grid>
                    </Grid>
                </form>
            </MDBox>

            <Dialog
                open={emailPreviewOpen}
                onClose={() => setEmailPreviewOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Annual Leave Email Preview</DialogTitle>

                <DialogContent dividers>
                    {["to", "cc", "subject"].map((field) => (
                        <div key={field} style={{ marginBottom: 16 }}>
                            <strong>{field.toUpperCase()}:</strong>

                            <div
                                contentEditable
                                suppressContentEditableWarning
                                onBlur={(e) =>
                                    setEmailPreviewData((prev) => ({
                                        ...prev,
                                        [field]: e.target.innerText.trim(),
                                    }))
                                }
                                style={{
                                    border: "1px solid #ccc",
                                    padding: "8px",
                                    minHeight: "30px",
                                    borderRadius: "4px",
                                    marginTop: "4px",
                                    backgroundColor: "#fff",
                                }}
                            >
                                {emailPreviewData[field]}
                            </div>
                        </div>
                    ))}

                    <div>
                        <strong>BODY:</strong>

                        <div
                            contentEditable
                            suppressContentEditableWarning
                            onBlur={(e) =>
                                setEmailPreviewData((prev) => ({
                                    ...prev,
                                    body: e.target.innerHTML,
                                }))
                            }
                            dangerouslySetInnerHTML={{ __html: emailPreviewData.body }}
                            style={{
                                border: "1px solid #ccc",
                                padding: "16px",
                                minHeight: "300px",
                                borderRadius: "4px",
                                backgroundColor: "#f9f9f9",
                                fontFamily: "Segoe UI, sans-serif",
                                fontSize: "15px",
                                lineHeight: "1.6",
                                color: "#333",
                                marginTop: "4px",
                            }}
                        />
                    </div>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setEmailPreviewOpen(false)} disabled={loading}>
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        disabled={loading}
                        onClick={confirmAndSendAnnualLeaveMail}
                        sx={{
                            backgroundColor: "#1e40af",
                            color: "#fff",
                            "&:hover": {
                                backgroundColor: "#1d4ed8",
                            },
                        }}
                    >
                        {loading ? "Sending..." : "Send Approval Email"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Footer />
        </DashboardLayout>
    );
};

export default AnnualLeaveForm;